import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useEditor } from '../../context/EditorContext';
import { WpDialog, Field, SelectInput } from './DialogShell';
import { Button } from '../ui/button';
import { ColorField } from '../editor/ColorPicker';
import { GradientEditor } from '../editor/GradientEditor';
import { fillToCss, PATTERNS, patternCss } from '../../lib/colors';
import { normalizeGradient, describeGradient, gradientFromColor } from '../../lib/gradients';
import { strokeHasGradient, gradientStroke, tableBorderStroke, tableBorderProps } from '../../lib/fills';
import { Switch } from '../ui/switch';
import { Sparkles } from 'lucide-react';
import { pickLocalFile } from '../editor/ObjectContextMenu';
import { Undo2 } from 'lucide-react';
import { toast } from 'sonner';

/** Which property a fill target lives in for an object. */
const fillKeyFor = (obj, target) => {
  const isText = obj.type === 'textbox' || obj.type === 'text';
  if (target === 'stroke') return 'stroke';
  if (target === 'textFill') return 'textFill';
  if (target === 'background' || isText) return 'background';
  return 'fill';
};

const TAB_LABEL = { gradient: 'Gradient', pattern: 'Pattern', picture: 'Picture' };

/** The outline seen as a fill — the editor's starting point for a gradient outline. */
const strokeAsFill = (stroke) => (strokeHasGradient(stroke) ? { type: 'gradient', gradient: stroke.gradient } : stroke?.width > 0 ? { type: 'solid', color: stroke.color || '#000000' } : null);

/**
 * Fill Effects — gradient (the gradient creator), pattern and picture fills for the selected objects or the page.
 * Everything previews live on the publication while the dialog is open; one Undo step (Cancel puts the original back).
 * props: { target: 'fill' | 'background' | 'textFill' | 'stroke' | 'page', tab? }
 * `stroke` = the outline of the selected objects (gradient only): the gradient goes into `stroke.gradient`, weight and
 * dash are kept (a hidden outline gets a visible weight), the colour stays as the solid fallback.
 */
export const FillEffectsDialog = ({ props }) => {
  const { closeDialog, dispatch, rawDispatch, state, getSelectedObjects, getCurrentPage } = useEditor();
  const target = props.target || 'fill';
  const isStroke = target === 'stroke';
  const pageIndex = state.currentPageIndex;

  // Targets + their original fills, fixed at open (selection changes underneath do not matter)
  const targets = useMemo(() => {
    if (target === 'page') { const page = getCurrentPage(); return [{ kind: 'page', index: pageIndex, key: 'background', original: page.background ?? { type: 'solid', color: '#ffffff' } }]; }
    return getSelectedObjects().map((o) => {
      const key = fillKeyFor(o, target);
      // Tables keep their borders as flat props — adapt them to a stroke for the editor (see tableBorderStroke).
      if (isStroke && o.type === 'table') return { kind: 'object', id: o.id, key, table: true, original: tableBorderStroke(o.properties), color: o.properties?.color };
      return { kind: 'object', id: o.id, key, original: o.properties?.[key] ?? null, color: o.properties?.color };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const current = isStroke ? strokeAsFill(targets[0]?.original) : targets[0]?.original;

  const [tab, setTab] = useState(isStroke ? 'gradient' : props.tab || (current?.type === 'pattern' ? 'pattern' : current?.type === 'image' ? 'picture' : 'gradient'));
  const [gradient, setGradient] = useState(() => {
    if (current?.type === 'gradient' && current.gradient) return normalizeGradient(current.gradient);
    // start from the object's own colour so the first picture is already "its" gradient
    return gradientFromColor(current?.type === 'solid' && current.color ? current.color : (target === 'textFill' && targets[0]?.color) || '#7c3aed');
  });
  const [c1, setC1] = useState(current?.type === 'pattern' ? current.color || '#000000' : '#7c3aed');
  const [c2, setC2] = useState(current?.type === 'pattern' ? current.background || '#ffffff' : '#ffffff');
  const [pattern, setPattern] = useState(current?.pattern || 'dots');
  const [img, setImg] = useState(current?.image?.url || '');
  const [fit, setFit] = useState(current?.image?.fit || 'cover');
  // Outline only: the sweeping highlight (stroke.shimmer) — part of the preview so toggling it previews live too.
  const [shimmer, setShimmer] = useState(() => !!(isStroke && targets[0]?.original?.shimmer));

  const build = () => {
    if (tab === 'gradient') return isStroke ? { type: 'gradient', gradient, shimmer } : { type: 'gradient', gradient };
    if (tab === 'pattern') return { type: 'pattern', pattern, color: c1, background: c2 };
    return img ? { type: 'image', image: { url: img, fit }, color: '#ffffff' } : null; // no picture chosen yet → keep what is there
  };

  // One history snapshot for the whole edit; live changes go through rawDispatch (no per-change history entries).
  // (Guarded by a ref: StrictMode re-runs mount effects, and a second snapshot after the first preview would split the Undo step.)
  const snapshotTaken = useRef(false);
  useEffect(() => { if (snapshotTaken.current) return; snapshotTaken.current = true; dispatch({ type: 'PUSH_HISTORY' }); }, [dispatch]);
  const applied = useRef(null);
  const applyFill = (fill) => {
    targets.forEach((t) => {
      if (t.kind === 'page') rawDispatch({ type: 'UPDATE_PAGE', payload: { index: t.index, data: { background: fill } } });
      else if (isStroke) {
        const next = { ...gradientStroke(t.original, fill.gradient), shimmer: !!fill.shimmer };
        rawDispatch({ type: 'UPDATE_OBJECT_PROPS', payload: { ids: [t.id], props: t.table ? tableBorderProps(next) : { stroke: next } } });
      }
      else rawDispatch({ type: 'UPDATE_OBJECT_PROPS', payload: { ids: [t.id], props: { [t.key]: fill } } });
    });
  };
  const restore = () => {
    targets.forEach((t) => {
      if (t.kind === 'page') rawDispatch({ type: 'UPDATE_PAGE', payload: { index: t.index, data: { background: t.original } } });
      else if (t.table) rawDispatch({ type: 'UPDATE_OBJECT_PROPS', payload: { ids: [t.id], props: tableBorderProps(t.original) } });
      else rawDispatch({ type: 'UPDATE_OBJECT_PROPS', payload: { ids: [t.id], props: { [t.key]: t.original } } });
    });
    applied.current = null;
  };
  const preview = build();
  useEffect(() => {
    if (!targets.length) return;
    if (!preview) { if (applied.current) restore(); return; }
    const json = JSON.stringify(preview);
    if (json === applied.current) return;
    applied.current = json;
    applyFill(preview);
    // keyed on the inputs `preview` is built from (build() runs every render, so the object itself is never a stable
    // dependency); applyFill / restore only use the open-time targets and dispatch, and targets.length is fixed at open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, gradient, c1, c2, pattern, img, fit, shimmer]);

  const cancel = () => {
    restore();
    dispatch({ type: 'PUSH_HISTORY' }); // identical to the snapshot taken at open → just clears the dirty flag
    closeDialog();
  };
  const ok = () => {
    if (!targets.length) { toast.info('Select an object to apply the fill to.'); closeDialog(); return; }
    if (!preview) { cancel(); return; }
    const what = target === 'page' ? 'page background' : target === 'textFill' ? 'text fill' : isStroke ? (targets.length > 1 ? `outlines of ${targets.length} objects` : targets[0]?.table ? 'table borders' : 'outline') : targets.length > 1 ? `${targets.length} objects` : 'fill';
    toast.success(isStroke ? `Gradient applied to the ${what}` : `${TAB_LABEL[tab]} fill applied to the ${what}`, { description: tab === 'gradient' ? `${describeGradient(gradient)}. Ctrl+Z undoes it.` : 'Ctrl+Z undoes it.', icon: <Undo2 className="h-4 w-4" />, id: 'fill-effects' });
    closeDialog();
  };

  const previewCss = preview ? (preview.type === 'pattern' ? patternCss(preview.pattern, preview.color, preview.background) : fillToCss(preview, '#ffffff')) : 'transparent';
  const title = target === 'page' ? 'Page Background' : target === 'textFill' ? 'Text Fill Effects' : target === 'background' ? 'Text Box Fill Effects' : isStroke ? (targets.length === 1 && targets[0].table ? 'Table Border Gradient' : 'Outline Gradient') : 'Fill Effects';
  const what = target === 'page' ? 'the page' : target === 'textFill' ? 'the text' : isStroke ? (targets.length > 1 ? `the outlines of ${targets.length} objects` : targets.length ? (targets[0].table ? 'the borders and cell rules of the table' : 'the outline of the selected object') : 'nothing (select an object first)') : targets.length > 1 ? `${targets.length} objects` : targets.length ? 'the selected object' : 'nothing (select an object first)';

  return (
    <WpDialog
      name="fill-effects"
      title={title}
      description={targets.length ? `Previewing on ${what} as you edit — one Undo step. Cancel puts the original ${isStroke ? 'outline' : 'fill'} back.${isStroke ? (targets[0]?.table ? ' The border weight stays as it is (Table Design ▸ Weight).' : ' Weight and dashes stay as they are (Shape Outline ▸ Weight / Dashes).') : ''}` : 'Nothing is selected — pick an object or a page first.'}
      width="w-[440px]"
      dock="right"
      keepOpenOnOutsideClick
      onOk={ok}
      onCancel={cancel}
      okLabel="OK"
    >
      {!isStroke && <div className="flex gap-1 mb-3 bg-muted p-1 rounded-md" role="tablist" aria-label="Fill kind">{['gradient', 'pattern', 'picture'].map((v) => <button key={v} type="button" onClick={() => setTab(v)} className={`flex-1 h-7 rounded-sm text-xs transition-colors ${tab === v ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`} aria-selected={tab === v} role="tab" data-testid={`dialog-fill-effects-tab-${v}`}>{TAB_LABEL[v]}</button>)}</div>}

      {tab === 'gradient' && <GradientEditor value={gradient} onChange={setGradient} testId="fill-effects-gradient" />}
      {isStroke && (
        <label className="mt-3 flex items-start justify-between gap-3 rounded-md border border-border bg-muted/40 px-3 py-2 cursor-pointer" data-testid="fill-effects-shimmer-row">
          <span className="min-w-0">
            <span className="flex items-center gap-1.5 text-xs font-semibold text-foreground"><Sparkles className="h-3.5 w-3.5" aria-hidden="true" />Shimmer highlight</span>
            <span className="block text-[11px] text-muted-foreground leading-snug">A soft highlight sweeps across the outline on screen. Print, PDF and picture exports stay still.</span>
          </span>
          <Switch checked={shimmer} onCheckedChange={(on) => setShimmer(!!on)} aria-label="Shimmer highlight" data-testid="fill-effects-shimmer-toggle" />
        </label>
      )}

      {tab !== 'gradient' && (
        <div className="space-y-3">
          {tab === 'pattern' && (
            <>
              <div className="grid grid-cols-2 gap-3"><Field label="Foreground"><ColorField value={c1} onChange={(v) => setC1(v || '#000000')} allowNoFill={false} testId="fill-effects-color1" /></Field><Field label="Background"><ColorField value={c2} onChange={(v) => setC2(v || '#ffffff')} allowNoFill={false} testId="fill-effects-color2" /></Field></div>
              <div className="grid grid-cols-7 gap-1">{PATTERNS.map((p) => <button key={p} type="button" onClick={() => setPattern(p)} className={`h-9 rounded-sm border ${pattern === p ? 'border-primary ring-1 ring-primary/40' : 'border-border'}`} style={{ background: patternCss(p, c1, c2) }} title={p} aria-label={`${p} pattern`} aria-pressed={pattern === p} data-testid={`fill-effects-pattern-${p}`} />)}</div>
            </>
          )}
          {tab === 'picture' && (
            <div className="space-y-2">
              <div className="flex gap-2"><Button type="button" variant="outline" size="sm" onClick={() => pickLocalFile((src) => setImg(src))} data-testid="fill-effects-picture-file">From File…</Button><Button type="button" variant="outline" size="sm" onClick={() => { cancel(); dispatch({ type: 'SHOW_IMAGE_GALLERY', payload: { mode: target === 'page' ? 'background' : 'fill' } }); }} data-testid="fill-effects-picture-online">Online Pictures…</Button></div>
              <Field label="Fit"><SelectInput value={fit} onChange={setFit} options={[{ value: 'cover', label: 'Fill' }, { value: 'contain', label: 'Fit' }, { value: 'stretch', label: 'Stretch' }, { value: 'tile', label: 'Tile' }]} className="w-full" testId="fill-effects-picture-fit" /></Field>
              {!img && <p className="text-[11px] text-muted-foreground">Choose a picture to preview it on the publication.</p>}
            </div>
          )}
          <div><div className="text-xs text-muted-foreground mb-1">Sample</div><div className="h-[96px] rounded-md border border-border wp-checker overflow-hidden"><div className="h-full w-full" style={{ background: previewCss, backgroundSize: preview?.type === 'image' && fit === 'tile' ? 'auto' : undefined }} data-testid="fill-effects-preview" /></div></div>
        </div>
      )}
    </WpDialog>
  );
};
